import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, RotateCcw, Maximize2 } from 'lucide-react';
import { sanitizeSvg } from '../lib/sanitizeSvg';

/**
 * MockupLightbox
 * --------------
 * Shared fullscreen viewer for ANY mockup surface (raster image, SVG string,
 * or any React node). Supports:
 *   • Click image / "Maximize" button → opens lightbox
 *   • Scroll-wheel + +/- buttons → zoom (0.5x – 6x)
 *   • Click + drag → pan (only when zoomed)
 *   • Double-click → reset zoom & pan
 *   • Pinch zoom (mobile / trackpad) via 2-finger pointer events
 *   • Esc → close
 *
 * Two exports:
 *   • <MockupLightbox open ... /> — the modal itself
 *   • <ZoomableImage src={...} /> — drop-in <img> that opens the lightbox on click
 */

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 6;
const ZOOM_STEP = 1.4;

export const MockupLightbox = ({ open, onClose, src, svgContent, title, alt = 'mockup' }) => {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // Track active drag/pinch as state so cursor + CSS transition update
  // immediately. The actual gesture math still goes through refs to avoid
  // re-rendering on every pointer move.
  const [isInteracting, setIsInteracting] = useState(false);
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const wrapRef = useRef(null);

  // Reset on every open via key — passing `open` as a key in the parent or
  // by gating render below. We reset state imperatively when the modal
  // mounts (open transitions false→true) without triggering the
  // set-state-in-effect rule: the parent unmounts/remounts via `if (!open)
  // return null` below, so initial state defaults apply.

  // Esc closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const zoomIn = useCallback(() => setZoom((z) => Math.min(MAX_ZOOM, z * ZOOM_STEP)), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(MIN_ZOOM, z / ZOOM_STEP)), []);
  const reset = useCallback(() => { setZoom(1); setOffset({ x: 0, y: 0 }); }, []);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
    setZoom((z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z * delta)));
  };

  // Pointer-events-based pan + pinch. Track up to 2 pointers in a Map so we
  // can compute a stable pinch distance.
  const pointersRef = useRef(new Map());

  const onPointerDown = (e) => {
    e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 1 && zoom > 1) {
      dragRef.current = { startX: e.clientX, startY: e.clientY, baseOffset: { ...offset } };
      setIsInteracting(true);
    } else if (pointersRef.current.size === 2) {
      const pts = Array.from(pointersRef.current.values());
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      pinchRef.current = { startDist: dist, startZoom: zoom };
      dragRef.current = null;
      setIsInteracting(true);
    }
  };

  const onPointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2 && pinchRef.current) {
      const pts = Array.from(pointersRef.current.values());
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const ratio = dist / pinchRef.current.startDist;
      setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, pinchRef.current.startZoom * ratio)));
    } else if (dragRef.current) {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setOffset({
        x: dragRef.current.baseOffset.x + dx,
        y: dragRef.current.baseOffset.y + dy,
      });
    }
  };

  const onPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size === 0) {
      dragRef.current = null;
      setIsInteracting(false);
    }
  };

  // Mount/unmount on open toggle so the inner state always starts fresh.
  if (!open) return null;

  // Render wrapper used inside the conditional return below.
  // Note: state-resetting via mount avoids the set-state-in-effect rule.
  // The toolbar / stage live inside this block.

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/95 flex flex-col"
      data-testid="mockup-lightbox"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between p-3 text-white text-sm">
        <div className="truncate pr-3">
          {title || 'Mockup'} · <span className="text-white/70">{Math.round(zoom * 100)}%</span>
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={zoomOut}
            className="p-2 rounded hover:bg-white/10"
            title="Zoom out"
            data-testid="lightbox-zoom-out"
          >
            <ZoomOut size={18} />
          </button>
          <button
            type="button"
            onClick={zoomIn}
            className="p-2 rounded hover:bg-white/10"
            title="Zoom in"
            data-testid="lightbox-zoom-in"
          >
            <ZoomIn size={18} />
          </button>
          <button
            type="button"
            onClick={reset}
            className="p-2 rounded hover:bg-white/10"
            title="Reset"
            data-testid="lightbox-reset"
          >
            <RotateCcw size={18} />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded hover:bg-white/10"
            title="Close (Esc)"
            data-testid="lightbox-close"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Stage */}
      <div
        ref={wrapRef}
        className="flex-1 overflow-hidden flex items-center justify-center select-none"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={reset}
        style={{
          cursor: zoom > 1 ? (isInteracting ? 'grabbing' : 'grab') : 'default',
          touchAction: 'none',
        }}
      >
        <div
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
            transition: isInteracting ? 'none' : 'transform 80ms ease-out',
            maxWidth: '90vw',
            maxHeight: '82vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {svgContent ? (
            <div
              style={{ maxWidth: '90vw', maxHeight: '82vh', filter: 'drop-shadow(0 6px 24px rgba(0,0,0,0.5))' }}
              dangerouslySetInnerHTML={{ __html: sanitizeSvg(svgContent) }}
            />
          ) : (
            <img
              src={src}
              alt={alt}
              draggable={false}
              style={{
                maxWidth: '90vw',
                maxHeight: '82vh',
                objectFit: 'contain',
                filter: 'drop-shadow(0 6px 24px rgba(0,0,0,0.5))',
              }}
            />
          )}
        </div>
      </div>

      {/* Hint */}
      <div className="text-center text-white/60 text-xs pb-3">
        Scroll or pinch to zoom · drag to pan · double-click to reset · Esc to close
      </div>
    </div>
  );
};

/**
 * <ZoomableImage> — drop-in replacement for <img>. Hovering reveals a small
 * "Maximize" badge in the corner; clicking the image (or badge) opens the
 * shared lightbox. Pass `svgContent` instead of `src` to render inline SVG.
 */
export const ZoomableImage = ({
  src,
  svgContent,
  alt = 'mockup',
  title,
  className = '',
  style,
  imgClassName,
  imgStyle,
  testId,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        className={`relative group cursor-zoom-in ${className}`}
        style={style}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={testId}
        {...rest}
      >
        {svgContent ? (
          <div
            className={imgClassName}
            style={imgStyle}
            dangerouslySetInnerHTML={{ __html: sanitizeSvg(svgContent) }}
          />
        ) : (
          <img
            src={src}
            alt={alt}
            className={imgClassName}
            style={imgStyle}
            draggable={false}
          />
        )}
        {/* Maximize badge — only visible on hover so it doesn't fight with
            other UI controls on the card. */}
        <span
          className="absolute top-2 right-2 bg-black/70 text-white rounded p-1.5 opacity-0 group-hover:opacity-100 transition pointer-events-none"
          aria-hidden
        >
          <Maximize2 size={14} />
        </span>
      </div>
      <MockupLightbox
        open={open}
        onClose={() => setOpen(false)}
        src={src}
        svgContent={svgContent}
        title={title}
        alt={alt}
      />
    </>
  );
};

export default MockupLightbox;
